/**
 * RAKNIDA · Search page AJAX
 * Updates results without a full page reload via the Section Rendering API.
 * - Form submit / Enter
 * - Live typing (debounced)
 * - Popular chips
 * - Pagination
 * URL stays in sync (pushState / replaceState + back/forward).
 */
(function () {
  'use strict';

  var DEBOUNCE_MS = 380;
  var MIN_CHARS = 2;

  function boot() {
    var root = document.querySelector('.rk-search');
    if (!root || root.getAttribute('data-rk-ajax') === '1') return;
    root.setAttribute('data-rk-ajax', '1');

    var sectionId = root.getAttribute('data-section-id');
    var results = root.querySelector('.rk-search-results');
    var form = root.querySelector('form.rk-search-field');
    var input = root.querySelector('.rk-search-input');
    if (!sectionId || !results || !form || !input) return;

    var searchPath = form.getAttribute('action') || '/search';
    var abortCtrl = null;
    var debounceTimer = null;
    var lastFetchedQ = null;
    var requestSeq = 0;

    function formParam(name) {
      var el = form.querySelector('input[name="' + name + '"]');
      return el ? el.value : '';
    }

    function syncWishButtons() {
      try {
        if (!window.RaknidaQV || typeof window.RaknidaQV.getWish !== 'function') return;
        var ids = window.RaknidaQV.getWish();
        document.querySelectorAll('.rk-pc-wish[data-wish-id]').forEach(function (btn) {
          var id = String(btn.getAttribute('data-wish-id'));
          var on = ids.some(function (w) {
            if (w == null) return false;
            if (typeof w === 'object') return String(w.id || w.product_id || '') === id;
            return String(w) === id;
          });
          btn.classList.toggle('on', on);
          btn.setAttribute('aria-pressed', on ? 'true' : 'false');
        });
      } catch (e) {}
    }

    function closePredictive() {
      var ps = root.querySelector('predictive-search');
      if (ps && typeof ps.close === 'function') {
        try {
          ps.close(true);
        } catch (e) {}
      }
      var dropdown = root.querySelector('[data-predictive-search]');
      if (dropdown) dropdown.innerHTML = '';
      document.body.classList.remove('predictive-search--focus');
    }

    function buildParams(overrides) {
      overrides = overrides || {};
      var params = new URLSearchParams();
      var q = overrides.q != null ? overrides.q : input.value.trim();
      if (q) params.set('q', q);

      var type = formParam('type');
      if (type) params.set('type', type);

      var prefix = formParam('options[prefix]');
      if (prefix) params.set('options[prefix]', prefix);

      if (overrides.page && Number(overrides.page) > 1) {
        params.set('page', String(overrides.page));
      }

      return params;
    }

    function publicUrl(params) {
      var qs = params.toString();
      return qs ? searchPath + '?' + qs : searchPath;
    }

    function fetchUrl(params) {
      var p = new URLSearchParams(params.toString());
      p.set('section_id', sectionId);
      return searchPath + '?' + p.toString();
    }

    function setLoading(on) {
      results.classList.toggle('is-loading', !!on);
      root.classList.toggle('rk-search--loading', !!on);
      input.setAttribute('aria-busy', on ? 'true' : 'false');
    }

    function escapeHtml(s) {
      return String(s || '')
        .replace(/&/g, '&amp;')
        .replace(/</g, '&lt;')
        .replace(/"/g, '&quot;');
    }

    function resultsAreEmpty() {
      return !!results.querySelector('.rk-search-empty');
    }

    function fetchSuggestProducts(q) {
      var url =
        '/search/suggest.json?q=' +
        encodeURIComponent(q) +
        '&resources[type]=product&resources[limit]=12';
      return fetch(url, { credentials: 'same-origin' })
        .then(function (res) {
          if (!res.ok) throw new Error('Suggest failed');
          return res.json();
        })
        .then(function (data) {
          return (((data || {}).resources || {}).results || {}).products || [];
        })
        .catch(function () {
          return [];
        });
    }

    function renderSuggestFallback(q, products) {
      if (!products.length) return false;
      var countLabel =
        products.length +
        (products.length === 1 ? ' suggested match' : ' suggested matches');
      var html =
        '<p class="rk-search-count">' +
        countLabel +
        ' for &ldquo;' +
        escapeHtml(q) +
        '&rdquo;</p>' +
        '<p class="rk-search-suggest-note">Some newer works appear here before the full catalogue search index catches up.</p>' +
        '<ul class="rk-search-grid" role="list">';

      products.forEach(function (p) {
        var href = String(p.url || '').split('?')[0] || '/products/' + p.handle;
        var imgObj = p.featured_image || p.image;
        var imgUrl = typeof imgObj === 'string' ? imgObj : imgObj && imgObj.url;
        if (imgUrl && imgUrl.indexOf('width=') === -1) {
          imgUrl += (imgUrl.indexOf('?') >= 0 ? '&' : '?') + 'width=600';
        }
        html +=
          '<li class="rk-search-grid-item rk-search-grid-item--product">' +
          '<a class="rk-search-suggest-card" href="' +
          escapeHtml(href) +
          '">';
        if (imgUrl) {
          html +=
            '<span class="rk-search-suggest-card__media"><img src="' +
            escapeHtml(imgUrl) +
            '" alt="" loading="lazy" width="600" height="900"></span>';
        }
        html +=
          '<span class="rk-search-suggest-card__body">' +
          '<span class="rk-search-suggest-card__title">' +
          escapeHtml(p.title) +
          '</span>';
        if (p.price) {
          html +=
            '<span class="rk-search-suggest-card__price">From $' +
            escapeHtml(p.price) +
            '</span>';
        }
        html += '</span></a></li>';
      });

      html += '</ul>';
      results.innerHTML = html;
      root.setAttribute('data-search-performed', 'true');
      root.setAttribute('data-search-terms', q);
      return true;
    }

    function applyResults(html, q) {
      var doc = new DOMParser().parseFromString(html, 'text/html');
      var next = doc.querySelector('.rk-search-results');
      if (!next) return false;

      results.innerHTML = next.innerHTML;

      var performed = !!q;
      root.setAttribute('data-search-performed', performed ? 'true' : 'false');
      root.setAttribute('data-search-terms', q || '');

      syncWishButtons();

      if (performed && q) {
        document.dispatchEvent(
          new CustomEvent('rk-search:performed', { detail: { term: q } })
        );
      }

      lastFetchedQ = q || '';
      return true;
    }

    function runSearch(opts) {
      opts = opts || {};
      var q = opts.q != null ? String(opts.q).trim() : input.value.trim();
      var page = opts.page || null;
      var historyMode = opts.history || 'push';
      var scroll = opts.scroll !== false;

      // Avoid duplicate in-flight identical queries (except pagination)
      var key = q + '::' + (page || '1');
      if (opts.skipDupe !== true && key === lastFetchedQ + '::' + (opts._pageKey || '1') && !page) {
        /* allow */
      }

      if (abortCtrl) {
        try {
          abortCtrl.abort();
        } catch (e) {}
      }
      abortCtrl = typeof AbortController !== 'undefined' ? new AbortController() : null;
      var seq = ++requestSeq;

      var params = buildParams({ q: q, page: page });
      setLoading(true);

      var fetchOpts = { credentials: 'same-origin' };
      if (abortCtrl) fetchOpts.signal = abortCtrl.signal;

      return fetch(fetchUrl(params), fetchOpts)
        .then(function (res) {
          if (!res.ok) throw new Error('Search failed');
          return res.text();
        })
        .then(function (html) {
          if (seq !== requestSeq) return;
          // Stale live-type response if the user kept typing
          if (historyMode === 'replace' && input.value.trim() !== q) return;

          var ok = applyResults(html, q);
          if (!ok) throw new Error('Invalid search markup');

          var finalize = function () {
            var url = publicUrl(params);
            if (historyMode === 'push') {
              history.pushState({ rkSearch: true, q: q, page: page || 1 }, '', url);
            } else if (historyMode === 'replace') {
              history.replaceState({ rkSearch: true, q: q, page: page || 1 }, '', url);
            }

            lastFetchedQ = q;
            setLoading(false);
            closePredictive();

            if (scroll) {
              var top = results.getBoundingClientRect().top + window.pageYOffset - 96;
              window.scrollTo({ top: Math.max(0, top), behavior: 'smooth' });
            }
          };

          if (q && resultsAreEmpty()) {
            return fetchSuggestProducts(q).then(function (products) {
              if (products.length) renderSuggestFallback(q, products);
              finalize();
            });
          }

          finalize();
          return;
        })
        .catch(function (err) {
          if (err && err.name === 'AbortError') return;
          setLoading(false);
          if (opts.fallback !== false) {
            window.location.href = publicUrl(buildParams({ q: q, page: page }));
          }
        });
    }

    /* Form submit → AJAX */
    form.addEventListener(
      'submit',
      function (e) {
        e.preventDefault();
        e.stopPropagation();
        if (debounceTimer) clearTimeout(debounceTimer);
        var q = input.value.trim();
        if (!q) {
          input.focus();
          return;
        }
        runSearch({ q: q, history: 'push', scroll: true });
      },
      true
    );

    /* Live typing (debounced) */
    input.addEventListener('input', function () {
      if (debounceTimer) clearTimeout(debounceTimer);
      debounceTimer = setTimeout(function () {
        var q = input.value.trim();
        if (!q) {
          runSearch({ q: '', history: 'replace', scroll: false });
          return;
        }
        if (q.length < MIN_CHARS) return;
        if (q === lastFetchedQ) return;
        runSearch({ q: q, history: 'replace', scroll: false });
      }, DEBOUNCE_MS);
    });

    /* Popular chips */
    root.addEventListener('click', function (e) {
      var chip = e.target.closest('.rk-search-chip');
      if (!chip) return;
      var href = chip.getAttribute('href') || '';
      if (!href) return;
      e.preventDefault();
      var url;
      try {
        url = new URL(href, window.location.origin);
      } catch (err) {
        return;
      }
      var q = url.searchParams.get('q') || chip.textContent.trim();
      input.value = q;
      if (debounceTimer) clearTimeout(debounceTimer);
      runSearch({ q: q, history: 'push', scroll: true });
    });

    /* Pagination inside results */
    results.addEventListener('click', function (e) {
      var link = e.target.closest('a');
      if (!link) return;
      var href = link.getAttribute('href') || '';
      if (!href || href.charAt(0) === '#') return;
      var url;
      try {
        url = new URL(href, window.location.origin);
      } catch (err) {
        return;
      }
      if (url.pathname.replace(/\/$/, '') !== searchPath.replace(/\/$/, '')) return;
      e.preventDefault();
      var q = url.searchParams.get('q') || input.value.trim();
      var page = url.searchParams.get('page');
      if (debounceTimer) clearTimeout(debounceTimer);
      runSearch({ q: q, page: page, history: 'push', scroll: true });
    });

    /* Back / forward */
    window.addEventListener('popstate', function () {
      if (!document.querySelector('.rk-search')) return;
      var params = new URLSearchParams(window.location.search);
      var q = params.get('q') || '';
      var page = params.get('page');
      input.value = q;
      if (debounceTimer) clearTimeout(debounceTimer);
      runSearch({ q: q, page: page, history: 'none', scroll: false, fallback: false });
    });
  }

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', boot);
  } else {
    boot();
  }
})();
